import { useEffect, useMemo, useState } from 'react'
import { chooseRenderQuality, hasWebGL } from '../lib/experience'

export function useReducedMotion(): boolean {
  const [reduced, setReduced] = useState(false)

  useEffect(() => {
    const query = window.matchMedia('(prefers-reduced-motion: reduce)')
    const update = () => setReduced(query.matches)
    update()
    query.addEventListener('change', update)
    return () => query.removeEventListener('change', update)
  }, [])

  return reduced
}

export function useRenderQuality() {
  const reducedMotion = useReducedMotion()
  const [viewport, setViewport] = useState(() => ({
    width: typeof window === 'undefined' ? 1200 : window.innerWidth,
    dpr: typeof window === 'undefined' ? 1 : window.devicePixelRatio,
  }))
  const [webglAvailable] = useState(() => (typeof document === 'undefined' ? true : hasWebGL()))

  useEffect(() => {
    let frame = 0
    const update = () => {
      cancelAnimationFrame(frame)
      frame = requestAnimationFrame(() => {
        setViewport({ width: window.innerWidth, dpr: window.devicePixelRatio })
      })
    }
    window.addEventListener('resize', update, { passive: true })
    return () => {
      window.removeEventListener('resize', update)
      cancelAnimationFrame(frame)
    }
  }, [])

  return useMemo(
    () => ({
      ...chooseRenderQuality(viewport.width, viewport.dpr, reducedMotion, webglAvailable),
      reducedMotion,
      webglAvailable,
    }),
    [reducedMotion, viewport, webglAvailable],
  )
}

export function useInView<T extends HTMLElement>(rootMargin = '220px') {
  const [node, setNode] = useState<T | null>(null)
  const [visible, setVisible] = useState(false)

  useEffect(() => {
    if (!node) return
    const observer = new IntersectionObserver(
      ([entry]) => setVisible(entry.isIntersecting),
      { rootMargin },
    )
    observer.observe(node)
    return () => observer.disconnect()
  }, [node, rootMargin])

  return { ref: setNode, visible }
}
